import type { Theme, FontPairing, Workflow } from "@structurify/shared-types";
import { MonoLabel } from "../components/primitives/index.ts";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  onNew: () => void;
  onBulk: () => void;
  onWorkflow: (workflow: Workflow) => void;
  onAudit: () => void;
  onScan: () => void;
  authed: boolean;
  creditsUsed: number;
  freeLimit: number;
  workflows: Workflow[];
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return "JUST NOW";
  if (mins < 60) return `${mins}M AGO`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}H AGO`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "YESTERDAY";
  if (days < 7) return `${days}D AGO`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
}

export function StageReturning({ theme, fonts, onNew, onBulk, onWorkflow, onAudit, onScan, authed, creditsUsed, freeLimit, workflows }: Props) {
  return (
    <div style={{ flex: 1, padding: "clamp(24px, 5vw, 64px)", overflow: "auto" }}>
      <style>{`
        @media (max-width: 760px) {
          .returning-hero { grid-template-columns: 1fr !important; }
          .returning-workflow-row { grid-template-columns: 1fr !important; gap: 6px !important; }
        }
      `}</style>
      <div style={{ maxWidth: 980, margin: "0 auto", opacity: 0, animation: "fadeUp 500ms forwards ease-out" }}>
        <div className="returning-hero" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(24px, 4vw, 48px)", alignItems: "end", marginBottom: 48 }}>
          <div>
            <MonoLabel theme={theme} fonts={fonts} size={10} style={{ display: "block", marginBottom: 12 }}>
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }).toUpperCase()}
            </MonoLabel>
            <h2 style={{ fontFamily: fonts.headerFamily === "serif" ? fonts.serif : fonts.sans, fontWeight: fonts.headerWeight, fontSize: 44, lineHeight: 1.05, letterSpacing: fonts.headerTracking, color: theme.ink, margin: 0 }}>
              Welcome back.
            </h2>
            <p style={{ fontFamily: fonts.sans, fontSize: 15, color: theme.inkSoft, lineHeight: 1.55, margin: "12px 0 0 0" }}>
              {authed
                ? workflows.length > 0
                  ? `${workflows.length} saved workflow${workflows.length !== 1 ? "s" : ""}. Run one or drop a new document.`
                  : "No saved workflows yet — process a document and save the schema."
                : `Free trial · ${creditsUsed} of ${freeLimit} documents used.`}
            </p>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            <div onClick={onNew} style={{ background: theme.ink, color: theme.docPaper, padding: "24px 24px", cursor: "pointer", border: `1px solid ${theme.ink}` }}>
              <MonoLabel theme={theme} fonts={fonts} size={9} color={theme.docPaper} style={{ opacity: 0.65 }}>ONE DOCUMENT</MonoLabel>
              <div style={{ fontFamily: fonts.sans, fontSize: 22, marginTop: 8, fontWeight: 500, letterSpacing: "-0.015em" }}>Drop a document →</div>
            </div>
            <div onClick={onBulk} style={{ background: "transparent", color: theme.ink, padding: "20px 24px", cursor: "pointer", border: `1px solid ${theme.ruleStrong}` }}>
              <MonoLabel theme={theme} fonts={fonts} size={9}>BULK</MonoLabel>
              <div style={{ fontFamily: fonts.sans, fontSize: 18, marginTop: 6, fontWeight: 500, letterSpacing: "-0.01em", color: theme.ink }}>Run a folder through a workflow →</div>
            </div>
          </div>
        </div>

        <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 12 }}>§ SAVED WORKFLOWS</MonoLabel>
        <div style={{ border: `1px solid ${theme.rule}` }}>
          {workflows.length === 0 ? (
            <div style={{ padding: "24px 22px", background: theme.panel, fontFamily: fonts.sans, fontSize: 14, color: theme.inkMute }}>
              No workflows saved yet.
            </div>
          ) : workflows.map((wf, i) => (
            <div
              key={wf.id}
              className="returning-workflow-row"
              onClick={() => onWorkflow(wf)}
              style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 24, alignItems: "center", padding: "18px 22px", borderBottom: i < workflows.length - 1 ? `1px solid ${theme.rule}` : "none", background: theme.panel, cursor: "pointer" }}
            >
              <div style={{ fontFamily: fonts.serif, fontSize: 17, color: theme.ink, fontWeight: 400 }}>{wf.name}</div>
              <span style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.inkMute, letterSpacing: "0.04em" }}>{wf.config.fieldCount} FIELDS</span>
              <span style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.inkMute, letterSpacing: "0.04em" }}>{timeAgo(wf.updatedAt)}</span>
              <span style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.inkMute, letterSpacing: "0.04em", width: 70, textAlign: "right" }}>
                {Math.round(wf.config.avgConfidence * 100)}% AVG
              </span>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 36, paddingTop: 20, borderTop: `1px solid ${theme.rule}`, display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: fonts.sans, fontSize: 12, color: theme.inkMute }}>
          <span>{authed ? `${creditsUsed} docs processed this session` : `Free trial · ${creditsUsed} of ${freeLimit} docs used`}</span>
          <span style={{ display: "flex", gap: 18 }}>
            <a onClick={onScan} style={{ color: theme.inkSoft, textDecoration: "none", cursor: "pointer" }}>Scan document</a>
            {authed && <a onClick={onAudit} style={{ color: theme.inkSoft, textDecoration: "none", cursor: "pointer" }}>Audit log</a>}
          </span>
        </div>
      </div>
    </div>
  );
}
